import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Stack } from '@mui/material';
import { Schedule, CheckCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLazyGetPosApprovalRequestQuery,
  useCancelApprovalRequestMutation,
} from '../../../services/discountApi';
import { clearPendingApproval } from '../../../store/discountSlice';
import { useDiscountOtpCycle } from '../../../hooks/useDiscountOtpCycle';
import { useToast } from '../../common/ToastProvider';
import DiscountOtpModal from './DiscountOtpModal';

// Fallback path only — reached when the cashier hands off from
// DiscountDrawer.js's inline "Awaiting Approval" view (explicit "Continue
// browsing", or drawer close) instead of waiting there for OTP, or when OTP
// isn't available for the org at all. See docs/ui-ux/discount-approval-otp.md
// §1.1 for why this split exists.
const ApprovalBanner = ({ onResumeHeldOrder }) => {
  const dispatch   = useDispatch();
  const showToast  = useToast();
  const [polling, setPolling] = useState(false);

  const {
    pendingApprovalRequestId, pendingApprovalDiscountName, pendingOrderId, awaitingInline,
  } = useSelector((s) => s.discount);

  const [triggerGetApproval] = useLazyGetPosApprovalRequestQuery();
  const [cancelApproval, { isLoading: isCancelling }] = useCancelApprovalRequestMutation();
  const intervalRef = useRef(null);

  const otp = useDiscountOtpCycle(pendingApprovalRequestId);

  const [modalOpen, setModalOpen] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [resolvedByName, setResolvedByName] = useState('');

  // Picked up an OTP cycle already in flight when it verifies here.
  useEffect(() => {
    if (otp.view === 'resolved') {
      setResolved(true);
      setResolvedByName(otp.resolvedByName);
      setModalOpen(false);
      showToast(`Discount approved by ${otp.resolvedByName || 'manager'}!`, 'success');
    }
  }, [otp.view, otp.resolvedByName, showToast]);

  useEffect(() => {
    if (!pendingApprovalRequestId) return;

    const poll = async () => {
      setPolling(true);
      try {
        const res = await triggerGetApproval(pendingApprovalRequestId, true).unwrap();
        const req = res?.data?.discount_approval_request;
        if (!req) return;

        if (req.status === 'approved') {
          clearInterval(intervalRef.current);
          setResolved(true);
          setResolvedByName(req.approved_by_name || '');
          setModalOpen(false);
          showToast(`Discount approved by ${req.approved_by_name || 'manager'}!`, 'success');
        } else if (req.status === 'rejected' || req.status === 'cancelled') {
          clearInterval(intervalRef.current);
          localStorage.removeItem('discountApproval');
          dispatch(clearPendingApproval());
          showToast('Discount request rejected by manager. Order is still on hold.', 'error');
        } else if (req.status === 'expired') {
          clearInterval(intervalRef.current);
          localStorage.removeItem('discountApproval');
          dispatch(clearPendingApproval());
          showToast('Discount request expired. Order is still on hold.', 'error');
        }
      } catch {
        // silent — keep polling
      } finally {
        setPolling(false);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 5000);
    return () => clearInterval(intervalRef.current);
  }, [pendingApprovalRequestId, triggerGetApproval, dispatch, showToast]);

  const handleCancel = async () => {
    try {
      await cancelApproval(pendingApprovalRequestId).unwrap();
      localStorage.removeItem('discountApproval');
      dispatch(clearPendingApproval());
      showToast('Approval request cancelled. Order is still on hold.', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to cancel approval request', 'error');
    }
  };

  const handleResumeBill = () => {
    const orderId = pendingOrderId;
    localStorage.removeItem('discountApproval');
    dispatch(clearPendingApproval());
    if (orderId) onResumeHeldOrder?.(orderId);
  };

  // Hidden while the drawer is still handling this inline (discountSlice.js's
  // awaitingInline) — otherwise it'd render behind the drawer showing the same
  // "awaiting approval" state redundantly. Takes over only after hand-off.
  if (!pendingApprovalRequestId || awaitingInline) return null;

  if (resolved) {
    return (
      <Box sx={{
        px: 1.5, py: 0.85,
        bgcolor: '#F0FDF4',
        borderTop: '1px solid #BBF7D0', borderBottom: '1px solid #BBF7D0',
        display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
      }}>
        <CheckCircle sx={{ fontSize: 15, color: '#16A34A', flexShrink: 0 }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#166534' }}>
            Approved by {resolvedByName || 'manager'}
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: '#16A34A' }}>Discount applied</Typography>
        </Box>
        <Button
          size="small"
          onClick={handleResumeBill}
          sx={{
            fontSize: '0.68rem', fontWeight: 800, color: '#fff', bgcolor: '#16A34A',
            borderRadius: '7px', textTransform: 'none', flexShrink: 0, minWidth: 0, px: 1.25,
            '&:hover': { bgcolor: '#15803D' },
          }}
        >
          Resume Bill →
        </Button>
      </Box>
    );
  }

  let subText = 'Order is on hold';
  if (otp.view === 'active') subText = `Cycle ${otp.cycle?.cycleNumber} of 3 — code sent to ${otp.cycle?.recipients?.length || 0} approvers`;
  else if (otp.view === 'expired') subText = `Code expired — cycle ${otp.cycle?.cycleNumber} of 3 used`;
  else if (otp.view === 'no_approver') subText = 'No verified approver available — admin dashboard only';
  else if (otp.view === 'exhausted') subText = 'Maximum OTP cycles reached — admin dashboard only';

  const otpButtonSx = {
    fontSize: '0.68rem', fontWeight: 700, color: '#4F46E5',
    border: '1px solid #4F46E5', borderRadius: '7px', textTransform: 'none',
    flexShrink: 0, minWidth: 0, px: 1,
    '&:hover': { bgcolor: '#EEF2FF' },
  };

  return (
    <>
      <Box sx={{
        px: 1.5, py: 0.85,
        bgcolor: '#FFFBEB',
        borderTop: '1px solid #FDE68A', borderBottom: '1px solid #FDE68A',
        display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
      }}>
        <Schedule sx={{ fontSize: 15, color: '#F59E0B', flexShrink: 0 }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400E' }}>
            {pendingApprovalDiscountName || 'Discount'} — awaiting manager approval
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {polling
              ? <CircularProgress size={10} sx={{ color: '#F59E0B' }} />
              : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FDE68A' }} />
            }
            <Typography noWrap sx={{ fontSize: '0.62rem', color: '#D97706' }}>{subText}</Typography>
          </Stack>
        </Box>

        {otp.view === 'idle' && (
          <Button size="small" onClick={otp.start} disabled={otp.isStarting} sx={otpButtonSx}>
            {otp.isStarting ? <CircularProgress size={12} /> : 'Text a manager'}
          </Button>
        )}
        {otp.view === 'active' && (
          <Button size="small" onClick={() => setModalOpen(true)} sx={otpButtonSx}>
            Enter code
          </Button>
        )}
        {otp.view === 'expired' && otp.canStartNewCycle && (
          <Button size="small" onClick={otp.start} disabled={otp.isStarting} sx={otpButtonSx}>
            {otp.isStarting ? <CircularProgress size={12} /> : 'New cycle'}
          </Button>
        )}

        <Button
          size="small"
          onClick={handleCancel}
          disabled={isCancelling}
          sx={{
            fontSize: '0.68rem', fontWeight: 700, color: '#EF4444',
            textTransform: 'none', flexShrink: 0, minWidth: 0,
            px: { xs: 0.75, md: 1 },
            '&:hover': { bgcolor: '#FEF2F2' },
          }}
        >
          {isCancelling ? <CircularProgress size={12} color="error" /> : 'Cancel'}
        </Button>
      </Box>

      <DiscountOtpModal
        open={modalOpen && otp.view === 'active'}
        onClose={() => setModalOpen(false)}
        cycle={otp.cycle}
        secondsLeft={otp.secondsLeft}
        resendCooldown={otp.resendCooldown}
        isResending={otp.isResending}
        isVerifying={otp.isVerifying}
        onVerify={otp.verify}
        onResend={otp.resend}
      />
    </>
  );
};

export default ApprovalBanner;
